/**
 * 個人ショット分析データ生成（自前・GPL コードは型のみ参照）。
 *
 * 物理結果と狙い情報から「ショット精度」「出し成功率」「ポケット別」の
 * 元データを生成する。ルール判定 (ShotObservation) には影響しない。
 *
 * GPL 封じ込め: Table は `import type` のみ（runtime に GPL コードを直接 import しない）。
 * Table インスタンス操作は world.ts が公開する高レベル関数（getPocketAims）と
 * 数値読みだけに留め、新たな GPL 接続面を作らない。
 *
 * ポケットID の規約は `getPocketAims()` の配列順:
 *   0: NW corner / 1: NE corner / 2: SW corner / 3: SE corner / 4: N side / 5: S side
 * クライアント/サーバー双方で同じ規約を使う（クライアントが計算してサーバーへ送る）。
 */

import type { Table } from '../engine/table.js';
import { getPocketAims, type PocketAim } from './world.js';

/** 撞く直前に保存する文脈（手球の出発位置）。 */
export interface AnalyticsContext {
  readonly cueBeforeX: number;
  readonly cueBeforeY: number;
}

/** 1 ショットの分析データ。 */
export interface ShotAnalytics {
  /** 手球の直線移動距離 [m]（撞く前→静止後）。簡略指標で十分（軌跡長は不要）。 */
  readonly cuePathLengthM: number;
  /** 落球毎のポケットID（0..5・getPocketAims 順）。落ちた順は保証しない（集計目的）。 */
  readonly pocketPottedIds: readonly number[];
  /** 出し評価: 手球→次球 と 次球→最寄ポケット狙点 のなす角 [deg]。
   *  小さいほど厚く狙える＝出しが良い。次球なし/手球落ち/盤上外 で null。 */
  readonly nextLeaveAngleDeg: number | null;
  /** 出し評価: 手球→次球の距離 [m]。null なら計測不可。 */
  readonly nextLeaveDistanceM: number | null;
}

/** 撞く直前に文脈を取得する（手球座標を保存）。 */
export const beginAnalytics = (table: Table): AnalyticsContext => ({
  cueBeforeX: table.cueball.pos.x,
  cueBeforeY: table.cueball.pos.y,
});

/** 2D 距離二乗で最近接ポケット index を返す。 */
const nearestPocketIndex = (
  px: number,
  py: number,
  pockets: readonly PocketAim[],
): number => {
  let bestIdx = 0;
  let bestD = Infinity;
  for (let i = 0; i < pockets.length; i++) {
    const p = pockets[i]!;
    const dx = p.x - px;
    const dy = p.y - py;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      bestIdx = i;
    }
  }
  return bestIdx;
};

/**
 * 静止後にショット分析データを確定する。
 *
 * - pottedNumberedIds: ルール側 (ShotObservation) で確定済みの「今ショットで落ちた的玉id」
 * - nextTargetBallId: ルール側で次に狙うべき的玉id（9ボールなら lowest in play）。
 *   null なら出し評価をスキップ（次球がない＝gameOver 等）。
 *
 * 失敗時（球が見つからない・座標が異常）は当該フィールドのみ null とし、他は出す。
 */
export const finalizeAnalytics = (
  table: Table,
  ctx: AnalyticsContext,
  pottedNumberedIds: readonly number[],
  nextTargetBallId: number | null,
): ShotAnalytics => {
  const pockets = getPocketAims();

  // 1) 手球移動距離（直線・簡略・カーブ無視）
  let cuePathLengthM = 0;
  try {
    const dx = table.cueball.pos.x - ctx.cueBeforeX;
    const dy = table.cueball.pos.y - ctx.cueBeforeY;
    cuePathLengthM = Math.hypot(dx, dy);
  } catch {
    cuePathLengthM = 0;
  }

  // 2) 落球毎のポケットID（最近接）
  const pocketPottedIds: number[] = [];
  for (const id of pottedNumberedIds) {
    try {
      const ball = table.balls.find((b: { id: number }): boolean => b.id === id);
      if (!ball) continue;
      pocketPottedIds.push(nearestPocketIndex(ball.pos.x, ball.pos.y, pockets));
    } catch {
      // 単一球の失敗は他球の集計を止めない
      continue;
    }
  }

  // 3) 出し評価（次球までの角度・距離）
  let nextLeaveAngleDeg: number | null = null;
  let nextLeaveDistanceM: number | null = null;
  if (nextTargetBallId !== null) {
    try {
      const cue = table.cueball;
      const nextBall = table.balls.find(
        (b: { id: number }): boolean => b.id === nextTargetBallId,
      );
      const cueOnTable = typeof cue.onTable === 'function' ? cue.onTable() : true;
      const nextOnTable =
        nextBall && typeof nextBall.onTable === 'function' ? nextBall.onTable() : false;
      if (cueOnTable && nextBall && nextOnTable) {
        const ux = nextBall.pos.x - cue.pos.x;
        const uy = nextBall.pos.y - cue.pos.y;
        const dist = Math.hypot(ux, uy);
        if (dist > 1e-6) {
          nextLeaveDistanceM = dist;
          const pIdx = nearestPocketIndex(nextBall.pos.x, nextBall.pos.y, pockets);
          const p = pockets[pIdx]!;
          const vx = p.x - nextBall.pos.x;
          const vy = p.y - nextBall.pos.y;
          const vlen = Math.hypot(vx, vy);
          if (vlen > 1e-6) {
            const cosA = (ux * vx + uy * vy) / (dist * vlen);
            const clamped = Math.max(-1, Math.min(1, cosA));
            // u と v のなす角＝カット角（小さいほど厚い）。
            nextLeaveAngleDeg = (Math.acos(clamped) * 180) / Math.PI;
          }
        }
      }
    } catch {
      // 計測失敗は null のまま続行（他指標は出す）
    }
  }

  return {
    cuePathLengthM,
    pocketPottedIds,
    nextLeaveAngleDeg,
    nextLeaveDistanceM,
  };
};
